import { useEffect, useState } from "react";
import { trpc } from "@/lib/trpc";
import UpdateBanner from "@/components/UpdateBanner";
import AppHeader from "@/components/AppHeader";
import YearFolderReorgCard from "@/components/YearFolderReorgCard";
import ConsentVisibilityCard from "@/components/ConsentVisibilityCard";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  BarChart3,
  Database,
  ShieldCheck,
  AlertCircle,
  Search,
  Trash2,
  Loader2,
  Sparkles,
  Download,
  Printer,
  RefreshCw,
  Link2,
  Pause,
  Play,
  Fingerprint,
  ChevronRight,
  ChevronDown,
} from "lucide-react";
import { toast } from "@/lib/toast";

/**
 * Admin-only diagnostic page mirroring scripts/library-stats.mjs.
 * Surfaces what's in footage_files broken down by the dimensions that
 * drive Footage Search filtering, so a "no results" search can be
 * triaged at a glance:
 *  - missing subject_kind / patient_permission → backfill-subject-kind
 *  - missing ai_description / embedding → backfill-pegasus
 *  - low total count → maybe nothing's been uploaded yet
 *
 * Read-only — never modifies anything.
 */
/** Friendly labels for the analysis_error categories surfaced in stats. */
const ANALYSIS_CATEGORY_LABELS: Record<string, string> = {
  throttled: "Throttled (Bedrock rate limit)",
  "model-error": "Model error (transient, 424/5xx)",
  disk: "Disk space (/tmp)",
  timeout: "Timed out",
  drive: "Drive fetch",
  "no-content": "No content produced",
  other: "Other",
  "too-short": "Too short (< 4s)",
  "too-long": "Too long (> 60 min)",
  unsupported: "Unsupported / corrupt",
  "not-video": "Not a video (image / macOS ._ stub)",
  "too-large": "Too large (> 30 GB)",
};

export default function LibraryStats() {
  useAuth({ redirectOnUnauthenticated: true });

  const utils = trpc.useUtils();
  const statsQuery = trpc.footage.libraryStats.useQuery();
  const stats = statsQuery.data;

  // AI-analysis failures: which category (if any) is expanded to show the
  // list of individual failing clips. Fetched lazily — only when a category
  // is expanded.
  const [expandedFailureCat, setExpandedFailureCat] = useState<string | null>(null);
  const analysisFailuresQuery = trpc.footage.analysisFailures.useQuery(
    { category: expandedFailureCat ?? undefined, limit: 500 },
    { enabled: !!expandedFailureCat },
  );

  // ── Ingest job: kick off + poll ───────────────────────────────────
  const ingestStartMutation = trpc.footage.ingestStart.useMutation();
  const ingestStatusQuery = trpc.footage.ingestStatus.useQuery(undefined, {
    // Poll every 2s only while a job is in flight; otherwise dormant.
    refetchInterval: (q) => (q.state.data?.status === "running" ? 2000 : false),
  });
  const ingestState = ingestStatusQuery.data;
  const ingestRunning = ingestState?.status === "running";

  // Auto-refresh the stats card the moment a job finishes so the user
  // sees the new numbers without manually reloading.
  const [lastSeenFinishedAt, setLastSeenFinishedAt] = useState<number | null>(
    ingestState?.finishedAt ?? null,
  );
  useEffect(() => {
    if (!ingestState) return;
    if (ingestState.status === "done" || ingestState.status === "error") {
      if (ingestState.finishedAt !== lastSeenFinishedAt) {
        setLastSeenFinishedAt(ingestState.finishedAt);
        void utils.footage.libraryStats.invalidate();
        if (ingestState.status === "done") {
          toast.success(
            `Ingest complete — ${ingestState.ingested.toLocaleString()} new row(s) added.`,
          );
        } else if (ingestState.error) {
          toast.error(`Ingest failed: ${ingestState.error}`);
        }
      }
    }
  }, [ingestState, lastSeenFinishedAt, utils]);

  const [showIngestConfirm, setShowIngestConfirm] = useState(false);
  const handleIngestConfirm = async () => {
    setShowIngestConfirm(false);
    try {
      const res = await ingestStartMutation.mutateAsync();
      if (!res.started) {
        toast.info("An ingest job was already running.");
      } else {
        toast.info("Ingest started — this can take several minutes.");
      }
      // Kick the status poll immediately so the progress card appears
      // without waiting for the next refetch tick.
      void ingestStatusQuery.refetch();
    } catch (err: any) {
      toast.error(`Couldn't start ingest: ${err?.message ?? "unknown error"}`);
    }
  };

  // ── Refresh-metadata: kick off + poll ─────────────────────────────
  // Patches NULL classification fields (region / subject_name /
  // permission / etc.) on existing rows so legacy clips light up the
  // new chips + filters without a CLI trip.
  const refreshMetadataStartMutation = trpc.footage.refreshMetadataStart.useMutation();
  const refreshMetadataStatusQuery = trpc.footage.refreshMetadataStatus.useQuery(undefined, {
    refetchInterval: (q) => (q.state.data?.status === "running" ? 2000 : false),
  });
  const refreshMetadataState = refreshMetadataStatusQuery.data;
  const refreshMetadataRunning = refreshMetadataState?.status === "running";
  const [refreshLastSeenFinishedAt, setRefreshLastSeenFinishedAt] = useState<number | null>(
    refreshMetadataState?.finishedAt ?? null,
  );
  useEffect(() => {
    if (!refreshMetadataState) return;
    if (
      refreshMetadataState.status === "done" ||
      refreshMetadataState.status === "error"
    ) {
      if (refreshMetadataState.finishedAt !== refreshLastSeenFinishedAt) {
        setRefreshLastSeenFinishedAt(refreshMetadataState.finishedAt);
        void utils.footage.libraryStats.invalidate();
        if (refreshMetadataState.status === "done") {
          toast.success(
            `Metadata refresh complete — ${refreshMetadataState.rowsUpdated.toLocaleString()} row(s) updated` +
              (refreshMetadataState.namesFixed > 0
                ? `, including ${refreshMetadataState.namesFixed.toLocaleString()} patient/provider name(s) corrected to match their renamed folder.`
                : "."),
          );
        } else if (refreshMetadataState.error) {
          toast.error(`Metadata refresh failed: ${refreshMetadataState.error}`);
        }
      }
    }
  }, [refreshMetadataState, refreshLastSeenFinishedAt, utils]);

  const [showRefreshConfirm, setShowRefreshConfirm] = useState(false);
  const handleRefreshConfirm = async () => {
    setShowRefreshConfirm(false);
    try {
      const res = await refreshMetadataStartMutation.mutateAsync();
      if (!res.started) {
        toast.info("A metadata refresh was already running.");
      } else {
        toast.info("Refresh started — watching for updated rows…");
      }
      void refreshMetadataStatusQuery.refetch();
    } catch (err: any) {
      toast.error(`Couldn't start refresh: ${err?.message ?? "unknown error"}`);
    }
  };

  // ── Relink-orphans: kick off + poll ───────────────────────────────
  // Walks each orphan's drive_folder_id in Drive and writes the
  // matching file id back to the row — preserves ai_description /
  // transcript / tags. Runs BEFORE delete-orphans so anything still
  // orphan after this step is genuinely unrecoverable.
  const relinkOrphansStartMutation = trpc.footage.relinkOrphansStart.useMutation();
  const relinkOrphansStatusQuery = trpc.footage.relinkOrphansStatus.useQuery(undefined, {
    refetchInterval: (q) => (q.state.data?.status === "running" ? 2000 : false),
  });
  const relinkOrphansState = relinkOrphansStatusQuery.data;
  const relinkOrphansRunning = relinkOrphansState?.status === "running";
  const [relinkLastSeenFinishedAt, setRelinkLastSeenFinishedAt] = useState<number | null>(
    relinkOrphansState?.finishedAt ?? null,
  );
  useEffect(() => {
    if (!relinkOrphansState) return;
    if (
      relinkOrphansState.status === "done" ||
      relinkOrphansState.status === "error"
    ) {
      if (relinkOrphansState.finishedAt !== relinkLastSeenFinishedAt) {
        setRelinkLastSeenFinishedAt(relinkOrphansState.finishedAt);
        void utils.footage.libraryStats.invalidate();
        if (relinkOrphansState.status === "done") {
          const { relinked, ambiguous, notFound } = relinkOrphansState;
          const parts: string[] = [];
          parts.push(`${relinked.toLocaleString()} re-linked`);
          if (ambiguous > 0) parts.push(`${ambiguous} ambiguous`);
          if (notFound > 0) parts.push(`${notFound} no match`);
          toast.success(`Re-link complete — ${parts.join(", ")}.`);
        } else if (relinkOrphansState.error) {
          toast.error(`Re-link failed: ${relinkOrphansState.error}`);
        }
      }
    }
  }, [relinkOrphansState, relinkLastSeenFinishedAt, utils]);

  const [showRelinkConfirm, setShowRelinkConfirm] = useState(false);
  const handleRelinkConfirm = async () => {
    setShowRelinkConfirm(false);
    try {
      const res = await relinkOrphansStartMutation.mutateAsync();
      if (!res.started) {
        toast.info("A re-link job was already running.");
      } else {
        toast.info("Re-link started — walking Drive for matches…");
      }
      void relinkOrphansStatusQuery.refetch();
    } catch (err: any) {
      toast.error(`Couldn't start re-link: ${err?.message ?? "unknown error"}`);
    }
  };

  // ── Orphan-row cleanup ─────────────────────────────────────────────
  const deleteOrphansMutation = trpc.footage.deleteOrphans.useMutation();
  const [showOrphanConfirm, setShowOrphanConfirm] = useState(false);
  const handleOrphanDeleteConfirm = async () => {
    setShowOrphanConfirm(false);
    try {
      const res = await deleteOrphansMutation.mutateAsync();
      toast.success(`Deleted ${res.deleted.toLocaleString()} orphan row(s).`);
      void utils.footage.libraryStats.invalidate();
    } catch (err: any) {
      toast.error(`Delete failed: ${err?.message ?? "unknown error"}`);
    }
  };

  // ── Non-video junk cleanup (._ AppleDouble stubs, images) ──────────
  const deleteNonVideoMutation = trpc.footage.deleteNonVideoRows.useMutation();
  const [showNonVideoConfirm, setShowNonVideoConfirm] = useState(false);
  const nonVideoCount = stats?.nonVideoRows ?? 0;
  const handleNonVideoDeleteConfirm = async () => {
    setShowNonVideoConfirm(false);
    try {
      const res = await deleteNonVideoMutation.mutateAsync();
      toast.success(`Removed ${res.deleted.toLocaleString()} non-video row(s).`);
      void utils.footage.libraryStats.invalidate();
    } catch (err: any) {
      toast.error(`Cleanup failed: ${err?.message ?? "unknown error"}`);
    }
  };

  // Two actionable orphan counts (both narrower than the broad
  // "missing drive_file_id" figure, so the buttons only claim what they
  // can actually do):
  //   relinkableCount — rows the re-link step can walk: missing a
  //                     drive_file_id but with a non-empty drive_folder_id.
  //                     Excludes NAS-native rows (empty drive_folder_id),
  //                     which re-link always skips.
  //   deletableCount  — rows safe to DELETE: no Drive file, no nas_path,
  //                     not a NAS-ingest session. Never includes NAS footage.
  const relinkableCount = stats?.orphans?.relinkable ?? 0;
  const deletableCount = stats?.orphans?.deletable ?? 0;

  // ── CSV / PDF exports ─────────────────────────────────────────────
  // Trigger a browser download from a tRPC-returned CSV string. Kept
  // client-side so a bad fetch can be toasted; for typical clinic sizes
  // (~10k rows) the inventory is a few MB — fits in memory easily.
  const downloadCsv = (filename: string, csv: string) => {
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    window.setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 200);
  };
  const [exportingStats, setExportingStats] = useState(false);
  const [exportingInventory, setExportingInventory] = useState(false);
  const handleExportStats = async () => {
    if (exportingStats) return;
    setExportingStats(true);
    try {
      const csv = await utils.footage.libraryStatsCsv.fetch();
      const stamp = new Date().toISOString().slice(0, 10);
      downloadCsv(`footage-library-stats-${stamp}.csv`, csv);
    } catch (err: any) {
      toast.error(`Stats export failed: ${err?.message ?? "unknown error"}`);
    } finally {
      setExportingStats(false);
    }
  };
  const handleExportInventory = async () => {
    if (exportingInventory) return;
    setExportingInventory(true);
    try {
      toast.info("Generating full inventory CSV — a few seconds for large libraries…");
      const csv = await utils.footage.libraryInventoryCsv.fetch();
      const stamp = new Date().toISOString().slice(0, 10);
      downloadCsv(`footage-library-inventory-${stamp}.csv`, csv);
    } catch (err: any) {
      toast.error(`Inventory export failed: ${err?.message ?? "unknown error"}`);
    } finally {
      setExportingInventory(false);
    }
  };

  // ── Pegasus AI-analysis backfill: estimate + start + poll ─────────
  const safeEstimateQuery = trpc.footage.pegasusBackfillEstimate.useQuery({
    scope: "safe",
  });
  const allEstimateQuery = trpc.footage.pegasusBackfillEstimate.useQuery({
    scope: "all",
  });
  const pegasusStartMutation = trpc.footage.pegasusBackfillStart.useMutation();
  const pegasusPauseMutation = trpc.footage.pegasusBackfillPause.useMutation();
  const pegasusResumeMutation = trpc.footage.pegasusBackfillResume.useMutation();
  const pegasusStatusQuery = trpc.footage.pegasusBackfillStatus.useQuery(undefined, {
    // Keep polling while running OR paused — the user might pause for
    // an upload and we want the live counts visible the whole time.
    refetchInterval: (q) => {
      const s = q.state.data?.status;
      return s === "running" || s === "pausing" || s === "paused" ? 3000 : false;
    },
  });
  const pegasusState = pegasusStatusQuery.data;
  // "Running" in the UI sense covers actively-processing AND paused —
  // both block the Start button (you can't start a second job) and
  // surface the live progress card.
  const pegasusActive =
    pegasusState?.status === "running" ||
    pegasusState?.status === "pausing" ||
    pegasusState?.status === "paused";
  const pegasusRunning = pegasusState?.status === "running";
  const pegasusPaused = pegasusState?.status === "paused";
  const pegasusPausing = pegasusState?.status === "pausing";

  const handlePegasusPause = async () => {
    try {
      const res = await pegasusPauseMutation.mutateAsync();
      if (res.paused) {
        toast.info("Pausing after current clip finishes…");
      } else {
        toast.info("Couldn't pause — job isn't running.");
      }
      void pegasusStatusQuery.refetch();
    } catch (err: any) {
      toast.error(`Pause failed: ${err?.message ?? "unknown error"}`);
    }
  };
  const handlePegasusResume = async () => {
    try {
      const res = await pegasusResumeMutation.mutateAsync();
      if (res.resumed) {
        toast.success("AI analysis resumed.");
      } else {
        toast.info("Couldn't resume — job isn't paused.");
      }
      void pegasusStatusQuery.refetch();
    } catch (err: any) {
      toast.error(`Resume failed: ${err?.message ?? "unknown error"}`);
    }
  };

  const [pegasusLastSeenFinishedAt, setPegasusLastSeenFinishedAt] = useState<
    number | null
  >(pegasusState?.finishedAt ?? null);
  useEffect(() => {
    if (!pegasusState) return;
    if (pegasusState.status === "done" || pegasusState.status === "error") {
      if (pegasusState.finishedAt !== pegasusLastSeenFinishedAt) {
        setPegasusLastSeenFinishedAt(pegasusState.finishedAt);
        void utils.footage.libraryStats.invalidate();
        void utils.footage.pegasusBackfillEstimate.invalidate();
        if (pegasusState.status === "done") {
          const parts = [`${pegasusState.succeeded.toLocaleString()} succeeded`];
          if (pegasusState.failed > 0) {
            parts.push(`${pegasusState.failed.toLocaleString()} failed`);
          }
          if (pegasusState.skipped > 0) {
            parts.push(`${pegasusState.skipped.toLocaleString()} skipped`);
          }
          toast.success(`AI analysis complete — ${parts.join(", ")}.`);
        } else if (pegasusState.error) {
          toast.error(`AI analysis failed: ${pegasusState.error}`);
        }
      }
    }
  }, [pegasusState, pegasusLastSeenFinishedAt, utils]);

  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [chosenScope, setChosenScope] = useState<"all" | "safe">("safe");
  const chosenEstimate =
    chosenScope === "safe" ? safeEstimateQuery.data : allEstimateQuery.data;

  const handleAnalysisConfirm = async () => {
    setShowAnalysisModal(false);
    try {
      const res = await pegasusStartMutation.mutateAsync({ scope: chosenScope });
      if (!res.started) {
        toast.info("AI analysis was already running.");
      } else {
        toast.info("AI analysis started — this can take a while.");
      }
      void pegasusStatusQuery.refetch();
    } catch (err: any) {
      toast.error(`Couldn't start analysis: ${err?.message ?? "unknown error"}`);
    }
  };

  // ETA — total seconds left = remaining rows * SECONDS_PER_CLIP. Use a
  // running average from the actual job once we have data points, else
  // fall back to the same 30s flat estimate the server uses.
  const pegasusEta = (() => {
    if (!pegasusState || pegasusState.status !== "running") return null;
    if (pegasusState.totalRows === 0) return null;
    const remaining = Math.max(0, pegasusState.totalRows - pegasusState.processedRows);
    let secPerRow = 30;
    if (pegasusState.processedRows > 0 && pegasusState.startedAt) {
      const elapsedSec = (Date.now() - pegasusState.startedAt) / 1000;
      secPerRow = Math.max(1, elapsedSec / pegasusState.processedRows);
    }
    const remainingSec = Math.round(remaining * secPerRow);
    if (remainingSec < 90) return `${remainingSec}s`;
    if (remainingSec < 3600) return `${Math.round(remainingSec / 60)}min`;
    const h = Math.floor(remainingSec / 3600);
    const m = Math.round((remainingSec % 3600) / 60);
    return `${h}h ${m}min`;
  })();

  // ── Drive-hash backfill: start + poll (non-destructive dedup step 1) ──
  const driveHashStartMutation = trpc.footage.driveHashBackfillStart.useMutation();
  const driveHashPauseMutation = trpc.footage.driveHashBackfillPause.useMutation();
  const driveHashResumeMutation = trpc.footage.driveHashBackfillResume.useMutation();
  const driveHashStatusQuery = trpc.footage.driveHashBackfillStatus.useQuery(
    undefined,
    {
      refetchInterval: (q) => {
        const s = q.state.data?.status;
        return s === "running" || s === "pausing" || s === "paused" ? 3000 : false;
      },
    },
  );
  const driveHashState = driveHashStatusQuery.data;
  const driveHashActive =
    driveHashState?.status === "running" ||
    driveHashState?.status === "pausing" ||
    driveHashState?.status === "paused";
  const driveHashRunning = driveHashState?.status === "running";
  const driveHashPaused = driveHashState?.status === "paused";
  const driveHashPausing = driveHashState?.status === "pausing";

  const [driveHashLastSeenFinishedAt, setDriveHashLastSeenFinishedAt] = useState<
    number | null
  >(driveHashState?.finishedAt ?? null);
  useEffect(() => {
    if (!driveHashState) return;
    if (driveHashState.status === "done" || driveHashState.status === "error") {
      if (driveHashState.finishedAt !== driveHashLastSeenFinishedAt) {
        setDriveHashLastSeenFinishedAt(driveHashState.finishedAt);
        void utils.footage.libraryStats.invalidate();
        if (driveHashState.status === "done") {
          const parts = [`${driveHashState.hashed.toLocaleString()} hashed`];
          if (driveHashState.duplicatesFound > 0) {
            parts.push(
              `${driveHashState.duplicatesFound.toLocaleString()} duplicate(s) found`,
            );
          }
          if (driveHashState.failed > 0) {
            parts.push(`${driveHashState.failed.toLocaleString()} failed`);
          }
          if (driveHashState.skipped > 0) {
            parts.push(`${driveHashState.skipped.toLocaleString()} skipped`);
          }
          toast.success(`Drive-hash backfill complete — ${parts.join(", ")}.`);
        } else if (driveHashState.error) {
          toast.error(`Drive-hash backfill failed: ${driveHashState.error}`);
        }
      }
    }
  }, [driveHashState, driveHashLastSeenFinishedAt, utils]);

  const handleDriveHashStart = async () => {
    try {
      const res = await driveHashStartMutation.mutateAsync();
      if (!res.started) {
        toast.info("Drive-hash backfill is already running.");
      } else {
        toast.info("Drive-hash backfill started — safe to leave this page.");
      }
      void driveHashStatusQuery.refetch();
    } catch (err: any) {
      toast.error(`Couldn't start backfill: ${err?.message ?? "unknown error"}`);
    }
  };
  const handleDriveHashPause = async () => {
    try {
      const res = await driveHashPauseMutation.mutateAsync();
      toast.info(
        res.paused
          ? "Pausing after the current file finishes…"
          : "Couldn't pause — job isn't running.",
      );
      void driveHashStatusQuery.refetch();
    } catch (err: any) {
      toast.error(`Pause failed: ${err?.message ?? "unknown error"}`);
    }
  };
  const handleDriveHashResume = async () => {
    try {
      const res = await driveHashResumeMutation.mutateAsync();
      toast.info(
        res.resumed ? "Backfill resumed." : "Couldn't resume — job isn't paused.",
      );
      void driveHashStatusQuery.refetch();
    } catch (err: any) {
      toast.error(`Resume failed: ${err?.message ?? "unknown error"}`);
    }
  };

  const driveHashEta = (() => {
    if (!driveHashState || driveHashState.status !== "running") return null;
    if (driveHashState.totalRows === 0) return null;
    if (driveHashState.processedRows === 0 || !driveHashState.startedAt) return null;
    const remaining = Math.max(
      0,
      driveHashState.totalRows - driveHashState.processedRows,
    );
    const elapsedSec = (Date.now() - driveHashState.startedAt) / 1000;
    const secPerRow = Math.max(0.5, elapsedSec / driveHashState.processedRows);
    const remainingSec = Math.round(remaining * secPerRow);
    if (remainingSec < 90) return `${remainingSec}s`;
    if (remainingSec < 3600) return `${Math.round(remainingSec / 60)}min`;
    const h = Math.floor(remainingSec / 3600);
    const m = Math.round((remainingSec % 3600) / 60);
    return `${h}h ${m}min`;
  })();

  // ── Drive-duplicate merge: preview + commit (dedup step 2) ────────────
  const mergeStartMutation = trpc.footage.mergeDriveDupesStart.useMutation();
  const mergePauseMutation = trpc.footage.mergeDriveDupesPause.useMutation();
  const mergeResumeMutation = trpc.footage.mergeDriveDupesResume.useMutation();
  const mergeCancelMutation = trpc.footage.mergeDriveDupesCancel.useMutation();
  const mergeStatusQuery = trpc.footage.mergeDriveDupesStatus.useQuery(undefined, {
    refetchInterval: (q) => {
      const s = q.state.data?.status;
      return s === "running" || s === "pausing" || s === "paused" ? 3000 : false;
    },
  });
  const mergeState = mergeStatusQuery.data;
  const mergeActive =
    mergeState?.status === "running" ||
    mergeState?.status === "pausing" ||
    mergeState?.status === "paused";
  const mergePaused = mergeState?.status === "paused";
  const mergePausing = mergeState?.status === "pausing";
  const [showMergeConfirm, setShowMergeConfirm] = useState(false);
  // When on, the merge also collapses Drive↔Drive duplicate index rows
  // (the "ambiguous" ones) — keeping one row per clip. Off by default so
  // the safe behavior (flag, don't touch) stays the default.
  const [collapseDriveTwins, setCollapseDriveTwins] = useState(false);

  const [mergeLastSeenFinishedAt, setMergeLastSeenFinishedAt] = useState<
    number | null
  >(mergeState?.finishedAt ?? null);
  useEffect(() => {
    if (!mergeState) return;
    if (mergeState.status === "done" || mergeState.status === "error") {
      if (mergeState.finishedAt !== mergeLastSeenFinishedAt) {
        setMergeLastSeenFinishedAt(mergeState.finishedAt);
        void utils.footage.libraryStats.invalidate();
        if (mergeState.status === "done") {
          if (mergeState.dryRun) {
            toast.success(
              `Preview complete — ${mergeState.mergeable.toLocaleString()} duplicate(s) ready to merge` +
                (mergeState.ambiguous > 0
                  ? `, ${mergeState.ambiguous.toLocaleString()} ambiguous (skipped)`
                  : "") +
                ".",
            );
          } else {
            toast.success(
              `Merge complete — ${mergeState.merged.toLocaleString()} duplicate(s) merged` +
                (mergeState.failed > 0
                  ? `, ${mergeState.failed.toLocaleString()} failed`
                  : "") +
                ".",
            );
          }
        } else if (mergeState.error) {
          toast.error(`Merge failed: ${mergeState.error}`);
        }
      }
    }
  }, [mergeState, mergeLastSeenFinishedAt, utils]);

  const handleMergeStart = async (dryRun: boolean) => {
    setShowMergeConfirm(false);
    try {
      const res = await mergeStartMutation.mutateAsync({ dryRun, collapseDriveTwins });
      if (!res.started) {
        toast.info("A merge job is already running.");
      } else {
        toast.info(
          dryRun
            ? "Preview started — no changes will be made."
            : "Merge started — safe to leave this page.",
        );
      }
      void mergeStatusQuery.refetch();
    } catch (err: any) {
      toast.error(`Couldn't start: ${err?.message ?? "unknown error"}`);
    }
  };
  const handleMergePause = async () => {
    try {
      const res = await mergePauseMutation.mutateAsync();
      toast.info(
        res.paused
          ? "Pausing after the current file finishes…"
          : "Couldn't pause — job isn't running.",
      );
      void mergeStatusQuery.refetch();
    } catch (err: any) {
      toast.error(`Pause failed: ${err?.message ?? "unknown error"}`);
    }
  };
  const handleMergeResume = async () => {
    try {
      const res = await mergeResumeMutation.mutateAsync();
      toast.info(res.resumed ? "Resumed." : "Couldn't resume — job isn't paused.");
      void mergeStatusQuery.refetch();
    } catch (err: any) {
      toast.error(`Resume failed: ${err?.message ?? "unknown error"}`);
    }
  };
  const handleMergeCancel = async () => {
    try {
      const res = await mergeCancelMutation.mutateAsync();
      toast.info(
        res.canceled
          ? "Abandoned — the job is cleared. You can start a fresh Preview."
          : "Nothing to abandon — no job is running.",
      );
      // Give the loop a beat to unwind + reset, then refresh state + stats.
      setTimeout(() => {
        void mergeStatusQuery.refetch();
        void utils.footage.libraryStats.invalidate();
      }, 800);
    } catch (err: any) {
      toast.error(`Abandon failed: ${err?.message ?? "unknown error"}`);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <UpdateBanner />
      <AppHeader />
      <main className="container py-8">
        <div className="max-w-4xl mx-auto space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-primary" />
                <CardTitle>Library Stats</CardTitle>
              </div>
              <p className="text-sm text-muted-foreground">
                Read-only snapshot of what&apos;s searchable in the footage_files
                table. Use this to diagnose &ldquo;no results&rdquo; searches —
                most of the time it&apos;s either rows missing classification
                (run <code>backfill-subject-kind</code>) or rows missing AI
                analysis (run <code>backfill-pegasus</code>).
              </p>
            </CardHeader>
          </Card>

          {statsQuery.isLoading && (
            <p className="text-sm text-muted-foreground text-center py-12">
              Loading library stats…
            </p>
          )}

          {statsQuery.error && (
            <Card className="border-red-200 dark:border-red-900">
              <CardContent className="pt-6">
                <p className="text-sm text-red-600 dark:text-red-400">
                  <AlertCircle className="inline w-4 h-4 mr-1" />
                  {statsQuery.error.message ?? "Failed to load stats."}
                </p>
              </CardContent>
            </Card>
          )}

          {stats && stats.total === 0 && (
            <Card>
              <CardContent className="pt-6 text-sm text-muted-foreground">
                The library is empty — no rows in footage_files. Nothing will
                appear in Footage Search until clips are uploaded through the
                wizard.
              </CardContent>
            </Card>
          )}

          {stats && stats.total > 0 && (
            <>
              {/* Headline */}
              <Card>
                <CardContent className="pt-6">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
                    Total rows
                  </p>
                  <p className="text-3xl font-semibold">
                    {stats.total.toLocaleString()}
                  </p>
                </CardContent>
              </Card>

              {/* Exports toolbar — small row of three buttons for grabbing
                  a snapshot of what's on this page. Hidden from print
                  output via the print-hidden class so the printed PDF
                  doesn't show buttons. */}
              <Card className="print-hidden">
                <CardContent className="pt-6 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={handleExportStats}
                    disabled={exportingStats}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded border border-border bg-background hover:bg-muted disabled:opacity-50 transition-colors"
                  >
                    {exportingStats ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Download className="w-3.5 h-3.5" />
                    )}
                    Download stats (CSV)
                  </button>
                  <button
                    type="button"
                    onClick={handleExportInventory}
                    disabled={exportingInventory}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded border border-border bg-background hover:bg-muted disabled:opacity-50 transition-colors"
                    title="Every footage_files row including ai_description and transcript_text — the same data the search engine reads. May contain patient names; treat the file as sensitive."
                  >
                    {exportingInventory ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Download className="w-3.5 h-3.5" />
                    )}
                    Download full inventory (CSV)
                  </button>
                  <button
                    type="button"
                    onClick={() => window.print()}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded border border-border bg-background hover:bg-muted transition-colors"
                    title="Opens the browser print dialog. Choose 'Save as PDF' as the destination to get a PDF copy of this page."
                  >
                    <Printer className="w-3.5 h-3.5" />
                    Print / Save as PDF
                  </button>
                </CardContent>
              </Card>

              {/* Admin actions — clickable in-app equivalents of the
                  scripts/ingest-from-drive.mjs + scripts/cleanup-orphan-rows.mjs
                  CLI tools. Designed so admins never need a terminal for the
                  routine library-management flow. */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Admin actions</CardTitle>
                  <p className="text-xs text-muted-foreground">
                    Run library-management tasks without dropping into a
                    terminal. Each action is idempotent and safe to re-run.
                  </p>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex items-start gap-3 p-3 rounded border border-border">
                    <div className="flex-1">
                      <div className="text-sm font-medium">
                        Discover &amp; ingest Drive footage
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        Walks your <code>*Patients</code> and <code>*Providers</code> Drive trees
                        and creates rows for every video that doesn&apos;t already
                        have one. Idempotent — safe to re-run.
                      </div>
                      <div className="text-[11px] text-muted-foreground/70 mt-1">
                        Last performed:{" "}
                        <span className="font-mono">
                          {formatLastPerformed(stats.lastPerformed.ingest)}
                        </span>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowIngestConfirm(true)}
                      disabled={ingestRunning || ingestStartMutation.isPending}
                      className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded border border-border bg-background hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {ingestRunning ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Search className="w-3.5 h-3.5" />
                      )}
                      {ingestRunning ? "Running…" : "Run ingest"}
                    </button>
                  </div>

                  {/* Refresh metadata on existing rows — UPDATE-only pass
                      that fills in the location chip / patient name /
                      permission fields on legacy clips that predate
                      those columns. Doesn't create new rows. */}
                  <div className="flex items-start gap-3 p-3 rounded border border-border">
                    <div className="flex-1">
                      <div className="text-sm font-medium">
                        Refresh metadata on existing rows
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        Fills in any missing classification (location,
                        patient name, permission tier) on rows that were
                        ingested before those fields existed. Idempotent;
                        already-complete rows are skipped.
                      </div>
                      <div className="text-[11px] text-muted-foreground/70 mt-1">
                        Last performed:{" "}
                        <span className="font-mono">
                          {formatLastPerformed(stats.lastPerformed.refreshMetadata)}
                        </span>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowRefreshConfirm(true)}
                      disabled={
                        refreshMetadataRunning ||
                        refreshMetadataStartMutation.isPending
                      }
                      className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded border border-border bg-background hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {refreshMetadataRunning ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <RefreshCw className="w-3.5 h-3.5" />
                      )}
                      {refreshMetadataRunning ? "Running…" : "Refresh metadata"}
                    </button>
                  </div>

                  {relinkableCount > 0 && (
                    <div className="flex items-start gap-3 p-3 rounded border border-border">
                      <div className="flex-1">
                        <div className="text-sm font-medium">
                          Re-link {relinkableCount.toLocaleString()} orphan row(s) to Drive
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                          For each row missing a <code>drive_file_id</code>,
                          walk its <code>drive_folder_id</code> in Drive and
                          match by filename. Writes the Drive id back so the
                          row keeps any AI description, transcript, and tags
                          already on it — recovering anything that lost its
                          link mid-upload. Run this BEFORE deleting orphans.
                        </div>
                        <div className="text-[11px] text-muted-foreground/70 mt-1">
                          Last performed:{" "}
                          <span className="font-mono">
                            {formatLastPerformed(stats.lastPerformed.relinkOrphans)}
                          </span>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowRelinkConfirm(true)}
                        disabled={
                          relinkOrphansRunning ||
                          relinkOrphansStartMutation.isPending
                        }
                        className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded border border-border bg-background hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {relinkOrphansRunning ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Link2 className="w-3.5 h-3.5" />
                        )}
                        {relinkOrphansRunning ? "Running…" : "Re-link orphans"}
                      </button>
                    </div>
                  )}

                  {deletableCount > 0 && (
                    <div className="flex items-start gap-3 p-3 rounded border border-border">
                      <div className="flex-1">
                        <div className="text-sm font-medium">
                          Delete {deletableCount.toLocaleString()} orphan row(s)
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                          Rows with no <code>drive_file_id</code>{" "}
                          <strong>and</strong> no <code>nas_path</code> — nothing
                          in Drive or on the NAS to point at, so they can&apos;t
                          be analyzed, previewed, or downloaded. NAS-native rows
                          are protected and never counted here. Run the re-link
                          step above first so any row with a matching Drive file
                          gets recovered; anything still orphan after that is
                          genuinely unrecoverable.
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowOrphanConfirm(true)}
                        disabled={
                          deleteOrphansMutation.isPending ||
                          ingestRunning ||
                          relinkOrphansRunning
                        }
                        className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded border border-red-200 dark:border-red-900 text-red-700 dark:text-red-400 bg-background hover:bg-red-50 dark:hover:bg-red-950 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        Delete orphans
                      </button>
                    </div>
                  )}

                  {/* Non-video junk cleanup — "._" AppleDouble stubs & images
                      that got ingested but can't be analyzed. Removes only the
                      index rows; the Drive/NAS file is untouched. */}
                  {nonVideoCount > 0 && (
                    <div className="flex items-start gap-3 p-3 rounded border border-border">
                      <div className="flex-1">
                        <div className="text-sm font-medium">
                          Clean up {nonVideoCount.toLocaleString()} non-video row(s)
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                          Rows whose file is a macOS <code>._</code> AppleDouble
                          stub (or other dot-hidden file) or an image
                          (<code>.heic</code>, <code>.jpg</code>, …) — not real
                          video, so Pegasus can never describe them. Removes only
                          the search-index row; the underlying Drive/NAS file is
                          left untouched. Leans the library and clears these out
                          of the analysis-failure list.
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowNonVideoConfirm(true)}
                        disabled={deleteNonVideoMutation.isPending}
                        className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded border border-red-200 dark:border-red-900 text-red-700 dark:text-red-400 bg-background hover:bg-red-50 dark:hover:bg-red-950 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        Clean up non-video rows
                      </button>
                    </div>
                  )}

                  {/* Pegasus AI analysis — costs real money so the button
                      opens a scope-picker modal that previews $ + minutes
                      per scope before any work starts. */}
                  {stats.needsPegasusBackfill > 0 && (
                    <div className="flex items-start gap-3 p-3 rounded border border-border">
                      <div className="flex-1">
                        <div className="text-sm font-medium">
                          Run AI analysis on{" "}
                          {stats.needsPegasusBackfill.toLocaleString()} row(s)
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                          Pegasus + Whisper + embeddings — unlocks semantic /
                          description / transcript matching in Footage Search.
                          Costs real money; you&apos;ll see the dollar estimate
                          before committing.
                        </div>
                        <div className="text-[11px] text-muted-foreground/70 mt-1">
                          Last performed:{" "}
                          <span className="font-mono">
                            {formatLastPerformed(stats.lastPerformed.pegasusBackfill)}
                          </span>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowAnalysisModal(true)}
                        disabled={pegasusActive || pegasusStartMutation.isPending}
                        className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded border border-primary/40 text-primary bg-background hover:bg-primary/5 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {pegasusActive ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Sparkles className="w-3.5 h-3.5" />
                        )}
                        {pegasusPaused
                          ? "Paused"
                          : pegasusPausing
                            ? "Pausing…"
                            : pegasusRunning
                              ? "Running…"
                              : "Run AI analysis"}
                      </button>
                    </div>
                  )}
                </CardContent>
              </Card>

              <ConsentVisibilityCard />

              <YearFolderReorgCard />

              {/* Live Pegasus progress — appears whenever a backfill
                  job is in flight. ETA is computed from actual per-row
                  wall time once we have data points, falling back to a
                  flat 30s/row estimate before the first row finishes. */}
              {pegasusState && pegasusActive && (
                <Card className="border-primary/30">
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      {pegasusPaused ? (
                        <Pause className="w-4 h-4 text-primary" />
                      ) : (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      )}
                      <span className="flex-1">
                        {pegasusPaused
                          ? "AI analysis paused"
                          : pegasusPausing
                            ? "Pausing AI analysis…"
                            : "AI analysis in progress"}
                      </span>
                      {/* Pause/Resume button — appears only when a job is
                          actually under way. Pause flips the state at the
                          next row boundary; the user can push an inline
                          upload through Bedrock during the pause window. */}
                      {pegasusPaused ? (
                        <button
                          type="button"
                          onClick={handlePegasusResume}
                          disabled={pegasusResumeMutation.isPending}
                          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded border border-primary/40 text-primary bg-background hover:bg-primary/5 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          <Play className="w-3 h-3" />
                          Resume
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={handlePegasusPause}
                          disabled={
                            pegasusPausing || pegasusPauseMutation.isPending
                          }
                          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded border border-border text-muted-foreground hover:text-foreground bg-background hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          <Pause className="w-3 h-3" />
                          {pegasusPausing ? "Pausing…" : "Pause"}
                        </button>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    <div>
                      Processed:{" "}
                      <strong>
                        {pegasusState.processedRows.toLocaleString()}
                      </strong>{" "}
                      / {pegasusState.totalRows.toLocaleString()}{" "}
                      <span className="text-muted-foreground">
                        ({pegasusState.succeeded.toLocaleString()} succeeded
                        {pegasusState.failed > 0
                          ? `, ${pegasusState.failed.toLocaleString()} failed`
                          : ""}
                        {pegasusState.skipped > 0
                          ? `, ${pegasusState.skipped.toLocaleString()} skipped`
                          : ""}
                        )
                      </span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary transition-all"
                        style={{
                          width: `${
                            pegasusState.totalRows > 0
                              ? (pegasusState.processedRows / pegasusState.totalRows) * 100
                              : 0
                          }%`,
                        }}
                      />
                    </div>
                    {pegasusEta && !pegasusPaused && (
                      <div className="text-xs text-muted-foreground">
                        Est. remaining: {pegasusEta}
                      </div>
                    )}
                    {pegasusPausing && (
                      <p className="text-xs text-muted-foreground pt-1">
                        Finishing the current clip — pause takes effect at
                        the next row.
                      </p>
                    )}
                    {pegasusPaused && pegasusState.pauseReason && (
                      <div className="mt-2 p-2 rounded border border-amber-200 dark:border-amber-900 bg-amber-50 dark:bg-amber-950/30 text-xs text-amber-900 dark:text-amber-200">
                        <strong>System auto-pause:</strong>{" "}
                        {pegasusState.pauseReason}
                      </div>
                    )}
                    {pegasusPaused && !pegasusState.pauseReason && (
                      <p className="text-xs text-muted-foreground pt-1">
                        Bedrock is free for inline uploads. Click Resume
                        when you&apos;re done to pick up where the
                        backfill left off.
                      </p>
                    )}
                    {!pegasusPausing && !pegasusPaused && (
                      <p className="text-xs text-muted-foreground pt-1">
                        Safe to leave this page — the job keeps running.
                      </p>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Live refresh-metadata progress. */}
              {refreshMetadataState && refreshMetadataState.status === "running" && (
                <Card className="border-primary/30">
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Refreshing metadata
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-1">
                    <div className="text-xs text-muted-foreground">
                      {refreshMetadataState.phase === "names"
                        ? "Step 2 of 2 — matching saved patient/provider names to their current folder names"
                        : "Step 1 of 2 — filling in missing details"}
                    </div>
                    <div>
                      Folders scanned:{" "}
                      <strong>
                        {refreshMetadataState.scannedFolders.toLocaleString()}
                      </strong>
                      {refreshMetadataState.totalFolders > 0 &&
                        ` / ${refreshMetadataState.totalFolders.toLocaleString()}`}
                    </div>
                    <div>
                      Rows updated:{" "}
                      <strong>
                        {refreshMetadataState.rowsUpdated.toLocaleString()}
                      </strong>
                      {refreshMetadataState.namesFixed > 0 &&
                        ` (${refreshMetadataState.namesFixed.toLocaleString()} name${refreshMetadataState.namesFixed === 1 ? "" : "s"} corrected)`}
                    </div>
                    <p className="text-xs text-muted-foreground pt-2">
                      Safe to leave the page — the job keeps running.
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Live relink-orphans progress. */}
              {relinkOrphansState && relinkOrphansState.status === "running" && (
                <Card className="border-primary/30">
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Re-linking orphans
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-1">
                    <div>
                      Orphans scanned:{" "}
                      <strong>
                        {relinkOrphansState.scannedOrphans.toLocaleString()}
                      </strong>
                      {relinkOrphansState.totalOrphans > 0 &&
                        ` / ${relinkOrphansState.totalOrphans.toLocaleString()}`}
                    </div>
                    <div>
                      Re-linked:{" "}
                      <strong>
                        {relinkOrphansState.relinked.toLocaleString()}
                      </strong>
                      {(relinkOrphansState.ambiguous > 0 ||
                        relinkOrphansState.notFound > 0) && (
                        <span className="text-muted-foreground">
                          {" "}
                          ({relinkOrphansState.ambiguous.toLocaleString()} ambiguous,{" "}
                          {relinkOrphansState.notFound.toLocaleString()} no match)
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground pt-2">
                      Safe to leave the page — the job keeps running.
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Live ingest progress — appears while a job runs and
                  shows live counters per the polled status. */}
              {ingestState && ingestState.status === "running" && (
                <Card className="border-primary/30">
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Ingest in progress
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-1">
                    <div>
                      Patients scanned:{" "}
                      <strong>
                        {ingestState.scannedPatients.toLocaleString()}
                      </strong>
                      {ingestState.totalPatients > 0 &&
                        ` / ${ingestState.totalPatients.toLocaleString()}`}
                    </div>
                    <div>
                      Providers scanned:{" "}
                      <strong>
                        {ingestState.scannedProviders.toLocaleString()}
                      </strong>
                      {ingestState.totalProviders > 0 &&
                        ` / ${ingestState.totalProviders.toLocaleString()}`}
                    </div>
                    <div>
                      New rows ingested:{" "}
                      <strong>
                        {ingestState.ingested.toLocaleString()}
                      </strong>{" "}
                      <span className="text-muted-foreground">
                        ({ingestState.byKind.patient.toLocaleString()} patient,{" "}
                        {ingestState.byKind.provider.toLocaleString()} provider)
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground pt-2">
                      Safe to leave this page — the job keeps running.
                      Come back any time to check progress.
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Backfill banner — only shown for the subject-kind
                  classification gap (no in-app button for that yet). The
                  Pegasus-backfill suggestion that used to live here was
                  removed: the "Run AI analysis" button in the Admin
                  actions card above is the canonical in-app path. */}
              {stats.needsSubjectKindBackfill > 0 && (
                <Card className="border-amber-200 bg-amber-50/50 dark:border-amber-900 dark:bg-amber-950/30">
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-amber-600" />
                      Action recommended
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    <div>
                      <strong>{stats.needsSubjectKindBackfill.toLocaleString()}</strong>{" "}
                      row(s) missing <code>subject_kind</code> or{" "}
                      <code>patient_permission</code> — invisible under the
                      default &ldquo;Safe to use&rdquo; filter. Run on the server:
                      <pre className="mt-1 text-xs bg-muted p-2 rounded overflow-x-auto">
                        node scripts/backfill-subject-kind.mjs --dry-run{"\n"}
                        node scripts/backfill-subject-kind.mjs
                      </pre>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* By subject_kind */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Database className="w-4 h-4" />
                    By subject kind
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <BreakdownTable
                    rows={stats.bySubjectKind.map((r) => ({
                      label: r.kind,
                      n: r.n,
                      total: stats.total,
                    }))}
                  />
                </CardContent>
              </Card>

              {/* By patient_permission */}
              {stats.patientTotal > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">
                      By patient permission ({stats.patientTotal.toLocaleString()} patient rows)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <BreakdownTable
                      rows={stats.byPatientPermission.map((r) => ({
                        label: r.perm,
                        n: r.n,
                        total: stats.patientTotal,
                      }))}
                    />
                    <div className="mt-3 pt-3 border-t border-border text-sm">
                      <span className="text-muted-foreground">
                        Special Instructions overlay:
                      </span>{" "}
                      <strong>{stats.specialInstructions.yes.toLocaleString()}</strong>{" "}
                      yes ·{" "}
                      <strong>{stats.specialInstructions.no.toLocaleString()}</strong>{" "}
                      no
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Search readiness */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Search readiness</CardTitle>
                  <p className="text-xs text-muted-foreground">
                    Which fields are populated across the {stats.total.toLocaleString()} rows. Each
                    field unlocks a different kind of match in Footage Search.
                  </p>
                </CardHeader>
                <CardContent>
                  <BreakdownTable
                    rows={[
                      {
                        label: "Has search_vector",
                        n: stats.readiness.hasSearchVector,
                        total: stats.total,
                        note: "title + tag queries",
                      },
                      {
                        label: "Has ai_description",
                        n: stats.readiness.hasAiDescription,
                        total: stats.total,
                        note: "Pegasus description queries",
                      },
                      {
                        label: "Has transcript_text",
                        n: stats.readiness.hasTranscript,
                        total: stats.total,
                        note: "Whisper spoken-word queries",
                      },
                      {
                        label: "Has embedding",
                        n: stats.readiness.hasEmbedding,
                        total: stats.total,
                        note: "semantic / fuzzy matching",
                      },
                      {
                        label: "Has drive_file_id",
                        n: stats.readiness.hasDriveFile,
                        total: stats.total,
                        note: "preview + bundle download",
                      },
                    ]}
                  />
                </CardContent>
              </Card>

              {/* AI-analysis failures — categorized so genuinely-retryable
                  failures (throttling, disk, timeout) are separated from
                  structurally-unsupported clips (too short/long, wrong
                  format) that will never succeed and no longer re-run. */}
              {(stats.analysis.failed > 0 || stats.analysis.unsupported > 0) && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">AI-analysis failures</CardTitle>
                    <p className="text-xs text-muted-foreground">
                      Why clips didn&apos;t get an AI description.{" "}
                      <strong>Retryable</strong> failures are worth re-running
                      once the cause clears; <strong>unsupported</strong> clips
                      are structural and are skipped on future runs so they stop
                      re-inflating this count.
                    </p>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex gap-4 text-sm">
                      <span>
                        Retryable:{" "}
                        <span className="font-semibold text-amber-700 dark:text-amber-500">
                          {stats.analysis.failed.toLocaleString()}
                        </span>
                      </span>
                      <span>
                        Unsupported:{" "}
                        <span className="font-semibold text-muted-foreground">
                          {stats.analysis.unsupported.toLocaleString()}
                        </span>
                      </span>
                    </div>
                    <div className="space-y-1">
                      {stats.analysis.byCategory.map((c) => {
                        const isOpen = expandedFailureCat === c.category;
                        return (
                          <div
                            key={`${c.terminal ? "skip" : "err"}-${c.category}`}
                            className="space-y-0.5"
                          >
                            <button
                              type="button"
                              onClick={() =>
                                setExpandedFailureCat(isOpen ? null : c.category)
                              }
                              className="w-full flex items-center justify-between text-sm text-left hover:bg-muted/50 rounded px-1 -mx-1 py-0.5 transition-colors"
                              aria-expanded={isOpen}
                            >
                              <span className="flex items-center gap-1.5">
                                {isOpen ? (
                                  <ChevronDown className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                                ) : (
                                  <ChevronRight className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                                )}
                                <span
                                  className={
                                    c.terminal
                                      ? "text-muted-foreground"
                                      : "text-amber-700 dark:text-amber-500"
                                  }
                                >
                                  {ANALYSIS_CATEGORY_LABELS[c.category] ?? c.category}
                                </span>
                                <span className="text-[11px] text-muted-foreground/70">
                                  {c.terminal ? "won't retry" : "retryable"}
                                </span>
                              </span>
                              <span className="font-mono tabular-nums">
                                {c.n.toLocaleString()}
                              </span>
                            </button>
                            {!isOpen && c.sample && (
                              <div
                                className="text-[11px] text-muted-foreground/70 font-mono truncate pl-5"
                                title={c.sample}
                              >
                                e.g. {c.sample}
                              </div>
                            )}
                            {isOpen && (
                              <div className="pl-5 pt-1 pb-2 space-y-1">
                                {analysisFailuresQuery.isLoading ? (
                                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                    Loading files…
                                  </div>
                                ) : (analysisFailuresQuery.data?.length ?? 0) === 0 ? (
                                  <div className="text-xs text-muted-foreground">
                                    No files to show.
                                  </div>
                                ) : (
                                  <>
                                    {analysisFailuresQuery.data!.map((f) => (
                                      <div
                                        key={f.id}
                                        className="text-xs flex items-start justify-between gap-3 border-b border-border/40 pb-1"
                                      >
                                        <div className="min-w-0">
                                          <div className="flex items-center gap-1.5">
                                            {f.driveFileId ? (
                                              <a
                                                href={`https://drive.google.com/file/d/${f.driveFileId}/view`}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="font-medium truncate hover:underline text-primary"
                                                title={f.name}
                                              >
                                                {f.name}
                                              </a>
                                            ) : (
                                              <span
                                                className="font-medium truncate"
                                                title={f.name}
                                              >
                                                {f.name}
                                              </span>
                                            )}
                                          </div>
                                          <div className="text-[11px] text-muted-foreground truncate">
                                            #{f.id}
                                            {f.subjectName ? ` · ${f.subjectName}` : ""}
                                            {f.region ? ` · ${f.region}` : ""}
                                          </div>
                                          <div
                                            className="text-[11px] text-muted-foreground/70 font-mono whitespace-pre-wrap break-words"
                                            title={f.error}
                                          >
                                            {f.error}
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                    {(analysisFailuresQuery.data?.length ?? 0) >= 500 && (
                                      <div className="text-[11px] text-muted-foreground/70">
                                        Showing first 500.
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Filter visibility */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4" />
                    Visible under each Footage Search filter
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <BreakdownTable
                    rows={[
                      {
                        label: "Safe to use (default)",
                        n: stats.visibility.safe,
                        total: stats.total,
                      },
                      {
                        label: "Safe + Check Before Posting",
                        n: stats.visibility.safeWithCbp,
                        total: stats.total,
                      },
                      {
                        label: "All (Safe unchecked)",
                        n: stats.visibility.all,
                        total: stats.total,
                      },
                    ]}
                  />
                </CardContent>
              </Card>

              {/* Dedup progress — Phase C monitoring */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Dedup progress</CardTitle>
                  <p className="text-xs text-muted-foreground">
                    {stats.dedup.hasContentHash === stats.total &&
                    stats.dedup.dupeGroups === 0 ? (
                      <span className="text-green-700 dark:text-green-500">
                        ✓ Complete — every row is hashed and no collisions remain.
                        &ldquo;One clip, one row&rdquo; is fully enforced.
                      </span>
                    ) : stats.dedup.hasContentHash < stats.total ? (
                      <>
                        Hash backfill in progress. Refresh this page every few
                        minutes to watch <code>Has content_hash</code> climb toward{" "}
                        {stats.total.toLocaleString()}.
                      </>
                    ) : stats.dedup.dupeGroups > 0 ? (
                      <>
                        Backfill complete — {stats.dedup.dupeGroups.toLocaleString()}{" "}
                        duplicate group(s) still waiting for the merge script to run.
                      </>
                    ) : (
                      <>Waiting for data.</>
                    )}
                  </p>
                </CardHeader>
                <CardContent>
                  <BreakdownTable
                    rows={[
                      {
                        label: "Has content_hash",
                        n: stats.dedup.hasContentHash,
                        total: stats.total,
                        note: "climbs as NAS + Drive backfills run",
                      },
                      {
                        label: "Has nas_path",
                        n: stats.dedup.hasNasPath,
                        total: stats.total,
                        note: "rows the scanner has seen on the NAS",
                      },
                      {
                        label: "Rows in dupe groups",
                        n: stats.dedup.rowsInDupeGroups,
                        total: stats.total,
                        note:
                          stats.dedup.dupeGroups > 0
                            ? `${stats.dedup.dupeGroups.toLocaleString()} group(s) — drop to 0 after merge`
                            : "no duplicates detected",
                      },
                    ]}
                  />
                </CardContent>
              </Card>

              {/* Dedup scope — shadow-duplicate remediation checklist */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Dedup scope</CardTitle>
                  <p className="text-xs text-muted-foreground">
                    {stats.dedup.unhashedDriveRows === 0 &&
                    stats.dedup.shadowRows === 0 &&
                    stats.dedup.nasEmptyFolderPatients === 0 ? (
                      <span className="text-green-700 dark:text-green-500">
                        ✓ Clear — no shadow duplicates or unlinked NAS patient
                        rows detected.
                      </span>
                    ) : (
                      <>
                        NAS-ingested rows that duplicate a Google Drive copy —
                        these are what surface as <code>_Unknown patient</code> in
                        a bundle. Each row below maps to one cleanup script; work
                        top-down.
                      </>
                    )}
                  </p>
                </CardHeader>
                <CardContent>
                  <BreakdownTable
                    rows={[
                      {
                        label: "Unhashed Drive rows",
                        n: stats.dedup.unhashedDriveRows,
                        total: stats.total,
                        note: "Drive rows without a content_hash — run backfill-drive-hashes.mjs first",
                      },
                      {
                        label: "Shadow rows",
                        n: stats.dedup.shadowRows,
                        total: stats.total,
                        note: "NAS rows with no hash + no Drive file — cleaned by reconcile-shadow-dupes.mjs",
                      },
                      {
                        label: "NAS patient rows w/ empty folder",
                        n: stats.dedup.nasEmptyFolderPatients,
                        total: stats.patientTotal,
                        note: 'the "_Unknown patient" symptom — trends to 0 as duplicates merge away',
                      },
                    ]}
                  />

                  {/* Step 1 action — hash the Drive rows (non-destructive).
                      This is the safe unlock: once Drive rows are hashed,
                      the duplicate NAS rows become detectable + mergeable. */}
                  <div className="mt-4 flex items-start gap-3 border-t border-border pt-3">
                    <div className="flex-1">
                      <div className="text-sm font-medium">
                        Backfill Drive hashes
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        Step 1 — SHA-256s the{" "}
                        {stats.dedup.unhashedDriveRows.toLocaleString()} unhashed
                        Drive row(s): hashes the genuinely-new ones and flags any
                        that duplicate an existing NAS row (the count the merge
                        step will remove). Non-destructive; safe to re-run.
                      </div>
                      <div className="text-[11px] text-muted-foreground/70 mt-1">
                        Last performed:{" "}
                        <span className="font-mono">
                          {formatLastPerformed(
                            stats.lastPerformed.driveHashBackfill,
                          )}
                        </span>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={handleDriveHashStart}
                      disabled={
                        driveHashActive ||
                        driveHashStartMutation.isPending ||
                        stats.dedup.unhashedDriveRows === 0
                      }
                      className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded border border-primary/40 text-primary bg-background hover:bg-primary/5 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {driveHashActive ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Fingerprint className="w-3.5 h-3.5" />
                      )}
                      {driveHashPaused
                        ? "Paused"
                        : driveHashPausing
                          ? "Pausing…"
                          : driveHashRunning
                            ? "Running…"
                            : stats.dedup.unhashedDriveRows === 0
                              ? "All hashed"
                              : "Backfill hashes"}
                    </button>
                  </div>

                  {/* Step 2 action — merge the detected duplicates
                      (destructive). Preview first; commit is behind a red
                      confirm dialog. */}
                  <div className="mt-3 flex items-start gap-3 border-t border-border pt-3">
                    <div className="flex-1">
                      <div className="text-sm font-medium">
                        Merge Drive duplicates
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        Step 2 — for each Drive row that duplicates a NAS row,
                        keep the NAS row (giving it the Drive folder + file so
                        &ldquo;_Unknown patient&rdquo; resolves) and delete the
                        redundant Drive row. Run <strong>Preview</strong> first —
                        it changes nothing.
                      </div>
                      <label className="mt-2 flex items-start gap-2 text-xs text-muted-foreground cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={collapseDriveTwins}
                          onChange={(e) => setCollapseDriveTwins(e.target.checked)}
                          disabled={mergeActive}
                          className="mt-0.5 accent-primary"
                        />
                        <span>
                          Also collapse duplicate <strong>Drive</strong> rows
                          (the &ldquo;ambiguous&rdquo; ones) — removes the extra
                          index entry so each clip shows once in search. Both
                          Drive files stay in Drive; cross-patient pairs are
                          still skipped.
                        </span>
                      </label>
                    </div>
                    <div className="shrink-0 flex flex-col gap-2">
                      <button
                        type="button"
                        onClick={() => handleMergeStart(true)}
                        disabled={mergeActive || mergeStartMutation.isPending}
                        className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded border border-primary/40 text-primary bg-background hover:bg-primary/5 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {mergeActive && mergeState?.dryRun ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Search className="w-3.5 h-3.5" />
                        )}
                        Preview
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowMergeConfirm(true)}
                        disabled={mergeActive || mergeStartMutation.isPending}
                        className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {mergeActive && mergeState && !mergeState.dryRun ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Trash2 className="w-3.5 h-3.5" />
                        )}
                        Merge
                      </button>
                    </div>
                  </div>
                  {/* Nightly safety net — runs steps 1 + 2 automatically. */}
                  <NightlyDedupLine />
                </CardContent>
              </Card>

              {/* Final tally of the last Drive-hash backfill (this server
                  session) — the live card below disappears when it ends. */}
              {driveHashState && !driveHashActive &&
                (driveHashState.status === "done" || driveHashState.status === "error") && (
                <Card>
                  <CardContent className="pt-4 text-sm">
                    <div className="font-medium">
                      Last Drive-hash backfill{" "}
                      {driveHashState.status === "error" ? "stopped with an error" : "finished"}
                      {driveHashState.finishedAt
                        ? ` ${new Date(driveHashState.finishedAt).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}`
                        : ""}
                    </div>
                    <div className="text-muted-foreground mt-1">
                      {driveHashState.processedRows.toLocaleString()} processed ·{" "}
                      <strong className="text-foreground">{driveHashState.hashed.toLocaleString()}</strong> hashed (Drive-only, done) ·{" "}
                      <strong className="text-foreground">{driveHashState.duplicatesFound.toLocaleString()}</strong> duplicates (for the merge step)
                      {driveHashState.failed > 0 && ` · ${driveHashState.failed.toLocaleString()} failed (re-run to retry)`}
                    </div>
                    {driveHashState.error && (
                      <div className="text-red-600 mt-1">{driveHashState.error}</div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Live Drive-hash backfill progress. */}
              {driveHashState && driveHashActive && (
                <Card className="border-primary/30">
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      {driveHashPaused ? (
                        <Pause className="w-4 h-4 text-primary" />
                      ) : (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      )}
                      <span className="flex-1">
                        {driveHashPaused
                          ? "Drive-hash backfill paused"
                          : driveHashPausing
                            ? "Pausing Drive-hash backfill…"
                            : "Drive-hash backfill in progress"}
                      </span>
                      {driveHashPaused ? (
                        <button
                          type="button"
                          onClick={handleDriveHashResume}
                          disabled={driveHashResumeMutation.isPending}
                          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded border border-primary/40 text-primary bg-background hover:bg-primary/5 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          <Play className="w-3 h-3" />
                          Resume
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={handleDriveHashPause}
                          disabled={
                            driveHashPausing || driveHashPauseMutation.isPending
                          }
                          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded border border-border text-muted-foreground hover:text-foreground bg-background hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          <Pause className="w-3 h-3" />
                          {driveHashPausing ? "Pausing…" : "Pause"}
                        </button>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    <div>
                      Processed:{" "}
                      <strong>
                        {driveHashState.processedRows.toLocaleString()}
                      </strong>{" "}
                      / {driveHashState.totalRows.toLocaleString()}{" "}
                      <span className="text-muted-foreground">
                        ({driveHashState.hashed.toLocaleString()} hashed
                        {driveHashState.duplicatesFound > 0
                          ? `, ${driveHashState.duplicatesFound.toLocaleString()} duplicate(s)`
                          : ""}
                        {driveHashState.failed > 0
                          ? `, ${driveHashState.failed.toLocaleString()} failed`
                          : ""}
                        {driveHashState.skipped > 0
                          ? `, ${driveHashState.skipped.toLocaleString()} skipped`
                          : ""}
                        )
                      </span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary transition-all"
                        style={{
                          width: `${
                            driveHashState.totalRows > 0
                              ? (driveHashState.processedRows /
                                  driveHashState.totalRows) *
                                100
                              : 0
                          }%`,
                        }}
                      />
                    </div>
                    {driveHashEta && !driveHashPaused && (
                      <div className="text-xs text-muted-foreground">
                        Est. remaining: {driveHashEta}
                      </div>
                    )}
                    {!driveHashPausing && !driveHashPaused && (
                      <p className="text-xs text-muted-foreground pt-1">
                        Safe to leave this page — the job keeps running.
                      </p>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Live Drive-duplicate merge / preview progress. */}
              {mergeState && mergeActive && (
                <Card className="border-primary/30">
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      {mergePaused ? (
                        <Pause className="w-4 h-4 text-primary" />
                      ) : (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      )}
                      <span className="flex-1">
                        {mergeState.dryRun
                          ? "Merge preview"
                          : "Merging duplicates"}
                        {mergePaused
                          ? " (paused)"
                          : mergePausing
                            ? " (pausing…)"
                            : ""}
                      </span>
                      {mergePaused ? (
                        <button
                          type="button"
                          onClick={handleMergeResume}
                          disabled={mergeResumeMutation.isPending}
                          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded border border-primary/40 text-primary bg-background hover:bg-primary/5 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          <Play className="w-3 h-3" />
                          Resume
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={handleMergePause}
                          disabled={mergePausing || mergePauseMutation.isPending}
                          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded border border-border text-muted-foreground hover:text-foreground bg-background hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          <Pause className="w-3 h-3" />
                          {mergePausing ? "Pausing…" : "Pause"}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={handleMergeCancel}
                        disabled={mergeCancelMutation.isPending}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded border border-red-300 text-red-700 dark:border-red-900 dark:text-red-400 bg-background hover:bg-red-50 dark:hover:bg-red-950/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <Trash2 className="w-3 h-3" />
                        Abandon
                      </button>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    <div>
                      Processed:{" "}
                      <strong>
                        {mergeState.processedRows.toLocaleString()}
                      </strong>{" "}
                      / {mergeState.totalRows.toLocaleString()}{" "}
                      <span className="text-muted-foreground">
                        (
                        {mergeState.dryRun
                          ? `${mergeState.mergeable.toLocaleString()} mergeable`
                          : `${mergeState.merged.toLocaleString()} merged`}
                        {mergeState.ambiguous > 0
                          ? `, ${mergeState.ambiguous.toLocaleString()} ambiguous`
                          : ""}
                        {mergeState.failed > 0
                          ? `, ${mergeState.failed.toLocaleString()} failed`
                          : ""}
                        )
                      </span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary transition-all"
                        style={{
                          width: `${
                            mergeState.totalRows > 0
                              ? (mergeState.processedRows /
                                  mergeState.totalRows) *
                                100
                              : 0
                          }%`,
                        }}
                      />
                    </div>
                    <p className="text-xs text-muted-foreground pt-1">
                      {mergeState.dryRun
                        ? "Preview only — nothing is being changed."
                        : "Safe to leave this page — the job keeps running."}
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Merge results — rows that didn't cleanly merge, listed so
                  they can be investigated. Persists after the job finishes
                  until the next run or a page refresh. */}
              {mergeState &&
                mergeState.notes.length > 0 && (
                  <Card className="border-amber-300 dark:border-amber-900">
                    <CardHeader>
                      <div className="flex items-start justify-between gap-3">
                        <CardTitle className="text-base">
                          {mergeState.dryRun ? "Preview" : "Merge"} — rows to
                          review
                        </CardTitle>
                        <button
                          type="button"
                          onClick={() =>
                            downloadMergeNotesCsv(
                              mergeState.notes,
                              mergeState.dryRun,
                            )
                          }
                          className="shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded border border-border bg-background hover:bg-muted transition-colors"
                        >
                          <Download className="w-3.5 h-3.5" />
                          Download CSV
                        </button>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {mergeState.ambiguous.toLocaleString()} ambiguous,{" "}
                        {mergeState.noTwin.toLocaleString()} no-twin,{" "}
                        {mergeState.failed.toLocaleString()} failed
                        {" · "}
                        {mergeState.dryRun
                          ? `${mergeState.mergeable.toLocaleString()} would merge cleanly`
                          : `${mergeState.merged.toLocaleString()} merged cleanly`}
                        {mergeState.trashedResolved > 0
                          ? ` (incl. ${mergeState.trashedResolved.toLocaleString()} where one copy was deleted in Drive)`
                          : ""}
                        {mergeState.misfiledResolved > 0
                          ? ` (incl. ${mergeState.misfiledResolved.toLocaleString()} filed under a category folder instead of the patient)`
                          : ""}
                        {mergeState.notes.length >= 200
                          ? " · showing first 200"
                          : ""}
                      </p>
                    </CardHeader>
                    <CardContent>
                      <div className="max-h-80 overflow-y-auto text-xs space-y-1">
                        {mergeState.notes.map((n) => (
                          <div
                            key={`${n.kind}-${n.rowId}`}
                            className="flex items-start gap-2 border-b border-border pb-1 last:border-0"
                          >
                            <span
                              className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] uppercase tracking-wide ${
                                n.kind === "ambiguous"
                                  ? "bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-300"
                                  : n.kind === "failed"
                                    ? "bg-red-100 text-red-800 dark:bg-red-950/50 dark:text-red-300"
                                    : "bg-muted text-muted-foreground"
                              }`}
                            >
                              {n.kind}
                            </span>
                            <div className="flex-1 min-w-0">
                              <div
                                className="font-mono truncate"
                                title={n.name}
                              >
                                #{n.rowId} · {n.name}
                              </div>
                              {(n.rowSubject || n.twinSubject) && (
                                <div className="text-muted-foreground">
                                  Patient: {n.rowSubject ?? "?"}
                                  {n.twinId
                                    ? ` · collides with: ${n.twinSubject ?? "?"}`
                                    : ""}
                                </div>
                              )}
                              <div className="text-muted-foreground">
                                {n.detail}
                              </div>
                              {(n.rowDriveFileId || n.twinDriveFileId || n.twinNasPath) && (
                                <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                                  {n.rowDriveFileId && (
                                    <a
                                      href={`https://drive.google.com/file/d/${n.rowDriveFileId}/view`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-primary hover:underline"
                                    >
                                      Open copy 1 ({n.rowSubject ?? "?"}) in Drive
                                    </a>
                                  )}
                                  {n.twinDriveFileId && (
                                    <a
                                      href={`https://drive.google.com/file/d/${n.twinDriveFileId}/view`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-primary hover:underline"
                                    >
                                      Open copy 2 ({n.twinSubject ?? "?"}) in Drive
                                    </a>
                                  )}
                                  {n.twinNasPath && (
                                    <span className="font-mono text-muted-foreground break-all" title="Copy 2's NAS location">
                                      NAS: {n.twinNasPath}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
            </>
          )}
        </div>
      </main>

      {/* Destructive merge confirmation. */}
      <AlertDialog open={showMergeConfirm} onOpenChange={setShowMergeConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Merge Drive duplicates?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span className="block">
                For every Drive row that duplicates a NAS row, this folds the
                Drive identity into the NAS row and then{" "}
                <strong>permanently deletes the Drive row</strong>. Each merge
                is recorded in the audit log, but there&apos;s no one-click
                undo.
              </span>
              <span className="block">
                Run <strong>Preview</strong> first if you haven&apos;t — it
                reports exactly how many rows would merge without changing
                anything.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleMergeStart(false)}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Merge duplicates
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Ingest confirmation — explains scope before kicking off the job. */}
      <AlertDialog open={showIngestConfirm} onOpenChange={setShowIngestConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Run Drive ingest?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span className="block">
                This walks every patient + provider folder in your Drive and
                creates database rows for any video file that isn&apos;t
                already tracked. Existing rows are left alone.
              </span>
              <span className="block">
                Can take several minutes depending on how much footage you
                have. Safe to navigate away — the job keeps running. Re-running
                later is harmless (already-tracked videos are skipped).
              </span>
              <span className="block text-xs">
                Note: this does NOT run AI analysis on the new rows. That&apos;s
                a separate step coming soon.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleIngestConfirm}>
              Run ingest
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* AI analysis scope picker — two radio cards, each with a live
          cost + time estimate fetched from the server. Confirm starts
          the background backfill. */}
      <AlertDialog open={showAnalysisModal} onOpenChange={setShowAnalysisModal}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Run AI analysis on which clips?</AlertDialogTitle>
            <AlertDialogDescription>
              Cost and time estimates are approximate (about $0.03 per clip,
              ~30 s per clip wall-time). You&apos;ll be charged AWS Bedrock
              for Pegasus + Whisper + Titan usage.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2 py-2">
            <label
              className={`flex items-start gap-3 p-3 rounded border cursor-pointer transition-colors ${
                chosenScope === "safe"
                  ? "border-primary bg-primary/5"
                  : "border-border hover:bg-muted/40"
              }`}
            >
              <input
                type="radio"
                name="scope"
                checked={chosenScope === "safe"}
                onChange={() => setChosenScope("safe")}
                className="mt-1"
              />
              <div className="flex-1">
                <div className="text-sm font-medium">
                  Safe-to-use clips only (recommended first run)
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  Provider A-Roll + Pre-Approved B-Roll. Smaller spend, lets
                  you see how useful semantic search is before committing
                  to the full library.
                </div>
                <div className="text-xs mt-2 font-mono">
                  {safeEstimateQuery.data
                    ? `${safeEstimateQuery.data.rows.toLocaleString()} clips · ~$${safeEstimateQuery.data.estimatedCostUsd.toFixed(2)} · ~${safeEstimateQuery.data.estimatedMinutes.toLocaleString()} min`
                    : "Loading estimate…"}
                </div>
              </div>
            </label>
            <label
              className={`flex items-start gap-3 p-3 rounded border cursor-pointer transition-colors ${
                chosenScope === "all"
                  ? "border-primary bg-primary/5"
                  : "border-border hover:bg-muted/40"
              }`}
            >
              <input
                type="radio"
                name="scope"
                checked={chosenScope === "all"}
                onChange={() => setChosenScope("all")}
                className="mt-1"
              />
              <div className="flex-1">
                <div className="text-sm font-medium">
                  All clips that need analysis
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  Everything with a Drive file but no AI description —
                  including Check Before Posting and Private clips.
                </div>
                <div className="text-xs mt-2 font-mono">
                  {allEstimateQuery.data
                    ? `${allEstimateQuery.data.rows.toLocaleString()} clips · ~$${allEstimateQuery.data.estimatedCostUsd.toFixed(2)} · ~${allEstimateQuery.data.estimatedMinutes.toLocaleString()} min`
                    : "Loading estimate…"}
                </div>
              </div>
            </label>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleAnalysisConfirm}
              disabled={!chosenEstimate || chosenEstimate.rows === 0}
            >
              Start —{" "}
              {chosenEstimate
                ? `${chosenEstimate.rows.toLocaleString()} clip(s) · ~$${chosenEstimate.estimatedCostUsd.toFixed(2)}`
                : "Loading…"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Refresh metadata confirmation — read-only operation, no data
          loss risk. Just explains what it does. */}
      {/* Re-link orphans confirmation — read-only on Drive, UPDATE-only
          on the DB, never deletes rows. Worth a confirmation because
          the user is choosing to walk every orphan's folder and write
          back ids, which can take a minute on a large library. */}
      <AlertDialog open={showRelinkConfirm} onOpenChange={setShowRelinkConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Re-link {relinkableCount.toLocaleString()} orphan row(s) to Drive?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span className="block">
                For each orphan, walks its Drive folder looking for a
                file whose name matches the row&apos;s renamed_name (or
                original_name as fallback). When exactly one match
                exists, writes the Drive file id back to the row.
              </span>
              <span className="block">
                Safe — never deletes or creates rows, only fills in a
                missing drive_file_id. Preserves any AI description,
                transcript, or tags already on the row. Rows with
                ambiguous matches (multiple Drive files with the same
                name) or no match at all are left alone.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRelinkConfirm}>
              Run re-link
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showRefreshConfirm} onOpenChange={setShowRefreshConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Refresh metadata on existing rows?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span className="block">
                For each existing row whose location / patient name /
                permission fields are still empty, this looks up the
                current values from Drive and fills them in. Already-
                complete rows are skipped.
              </span>
              <span className="block">
                Safe to run — never overwrites a populated field with
                stale data, never creates or deletes rows. Can take a
                few minutes for a large library; the job runs in the
                background and you can leave this page.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRefreshConfirm}>
              Run refresh
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Orphan deletion confirmation — irreversible, calls it out. */}
      <AlertDialog open={showOrphanConfirm} onOpenChange={setShowOrphanConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete {deletableCount.toLocaleString()} orphan row(s)?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span className="block">
                These rows have no <code>drive_file_id</code>{" "}
                <strong>and</strong> no <code>nas_path</code>, which means
                they can&apos;t be analyzed, previewed, or included in a
                bundle download. They&apos;re dead weight in the database.
                NAS-native rows (reachable via <code>nas_path</code>) are
                protected and won&apos;t be touched.
              </span>
              <span className="block">
                <strong>This deletes the rows permanently.</strong> Any
                descriptions or transcripts attached to them will be lost.
                Make sure you&apos;ve run the Drive ingest first — once new
                rows are in place, the orphans are demonstrably unrecoverable.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleOrphanDeleteConfirm}
              className="bg-red-600 hover:bg-red-700 text-white focus:ring-red-600"
            >
              Delete {deletableCount.toLocaleString()} row(s)
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Non-video cleanup confirmation — removes index rows only. */}
      <AlertDialog open={showNonVideoConfirm} onOpenChange={setShowNonVideoConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Clean up {nonVideoCount.toLocaleString()} non-video row(s)?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span className="block">
                These rows are macOS <code>._</code> AppleDouble stubs (or other
                dot-hidden files) and images (<code>.heic</code>,{" "}
                <code>.jpg</code>, …) — not real video, so they can never get an
                AI description and only clutter search + the failure list.
              </span>
              <span className="block">
                <strong>This removes the database rows only.</strong> The actual
                files in Google Drive / on the NAS are <strong>not</strong>{" "}
                touched — this just takes them out of the searchable index.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleNonVideoDeleteConfirm}
              className="bg-red-600 hover:bg-red-700 text-white focus:ring-red-600"
            >
              Clean up {nonVideoCount.toLocaleString()} row(s)
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/**
 * Format an epoch-ms timestamp as MM.DD.YY — matches the user's
 * existing date-folder convention. Returns "Never" for null so the
 * "Last performed" line stays one line regardless.
 */
function formatLastPerformed(ts: number | null): string {
  if (!ts) return "Never";
  const d = new Date(ts);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(d.getMonth() + 1)}.${pad(d.getDate())}.${String(d.getFullYear()).slice(-2)}`;
}

/**
 * Compact label / count / inline-bar table. The bar uses --primary so
 * it picks up the MN purple under the Footage Search theme wrapper.
 */
/** Build a CSV from the merge review notes and trigger a client-side
 *  download. The data is already in the client payload, so no server
 *  round-trip is needed. */
function downloadMergeNotesCsv(
  notes: Array<{
    kind: string;
    rowId: number;
    name: string;
    twinId?: number;
    twinName?: string;
    rowSubject?: string | null;
    twinSubject?: string | null;
    detail: string;
  }>,
  dryRun: boolean,
) {
  const esc = (v: unknown) => {
    const s = v === null || v === undefined ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const rows = [
    [
      "kind",
      "row_id",
      "row_name",
      "row_patient",
      "collides_with_id",
      "collides_with_name",
      "collides_with_patient",
      "reason",
    ],
    ...notes.map((n) => [
      n.kind,
      n.rowId,
      n.name,
      n.rowSubject ?? "",
      n.twinId ?? "",
      n.twinName ?? "",
      n.twinSubject ?? "",
      n.detail,
    ]),
  ];
  const csv = rows.map((r) => r.map(esc).join(",")).join("\r\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `merge-${dryRun ? "preview" : "commit"}-review-${new Date()
    .toISOString()
    .slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function BreakdownTable({
  rows,
}: {
  rows: Array<{ label: string; n: number; total: number; note?: string }>;
}) {
  return (
    <table className="w-full text-sm">
      <tbody>
        {rows.map((row) => {
          const pct = row.total === 0 ? 0 : (row.n / row.total) * 100;
          return (
            <tr key={row.label} className="border-b border-border last:border-0">
              <td className="py-1.5 pr-3">
                <div className="font-medium">{row.label}</div>
                {row.note && (
                  <div className="text-[11px] text-muted-foreground">{row.note}</div>
                )}
              </td>
              <td className="py-1.5 pr-3 text-right font-mono tabular-nums">
                {row.n.toLocaleString()}
              </td>
              <td className="py-1.5 pr-3 text-right text-xs text-muted-foreground tabular-nums w-12">
                {pct.toFixed(0)}%
              </td>
              <td className="py-1.5 w-32">
                <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/**
 * One-line status for the nightly dedup safety net (src/server/nightlyDedup.ts),
 * which runs Backfill Drive hashes + Merge Drive duplicates every night at
 * 2 AM Central and auto-merges up to `limit` pairs.
 */
function NightlyDedupLine() {
  const q = trpc.footage.nightlyDedupStatus.useQuery();
  const last = q.data?.last;
  const limit = q.data?.limit ?? 100;
  const when = last?.finishedAt
    ? new Date(last.finishedAt).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
    : null;
  let text: string;
  let tone = "text-muted-foreground";
  if (!last) {
    text = "Hasn't run yet — first run tonight at 2 AM.";
  } else if (last.outcome === "clean") {
    text = "Nothing to clean up.";
  } else if (last.outcome === "merged") {
    text = `Merged ${last.merged.toLocaleString()} duplicate${last.merged === 1 ? "" : "s"} automatically.`;
  } else if (last.outcome === "needs-review") {
    tone = "text-amber-700 dark:text-amber-400";
    text =
      last.mergeable > limit
        ? `Found ${last.mergeable.toLocaleString()} duplicates — more than the nightly limit of ${limit}, so nothing was merged. Run Preview → Merge above to review them.`
        : `Merged ${last.merged.toLocaleString()}; ${last.skippedUncertain.toLocaleString()} pair${last.skippedUncertain === 1 ? "" : "s"} skipped as uncertain — run Preview above to see which.`;
  } else if (last.outcome === "skipped-busy") {
    text = "Skipped — a dedup job was already running.";
  } else {
    tone = "text-red-700 dark:text-red-400";
    text = `Didn't finish: ${last.error ?? "unknown error"}. It will try again tomorrow night.`;
  }
  return (
    <div className="mt-3 border-t border-border pt-3 text-xs">
      <div className="text-sm font-medium">Nightly safety net</div>
      <div className="text-muted-foreground mt-0.5">
        Runs both steps automatically every night at 2 AM Central and merges up to {limit} duplicates on its own.
      </div>
      <div className={`mt-1 ${tone}`}>
        {when ? <span className="font-mono">{when}</span> : null}
        {when ? " — " : ""}
        {text}
      </div>
    </div>
  );
}
